import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import CostReportSearchProvider, { useCostReportSearch } from './search';
import { CostReportTopBar, CostReportDateSelector } from './components/top-bar';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import moment from 'moment';
import { Year } from '../../features/settings/workcalendar/types';
import {getAllEmployees} from '../../features/employees/short';
import {getWorkCalendar, getWorkCalendars} from '../../features/settings/workcalendar';
import CostReportGrid from './components/grid';
import { ExpandedGroupsRef } from '../../components/shared/grid/types';
import { useGridViewHandoff } from '../../hooks/useGridViewHandoff';
import useCostReportLoader from './loader';
import { useIntl } from 'react-intl';
import { getProjects } from '../../features/projects/filter';
import { Cost, CostReportItem } from '../../features/cost-report/types';
import { getCost, deleteCost as deleteCostAction } from '../../features/cost-report/slice';
import {setIndicatorLoading} from '../../features/indicator';
import PageWrapperV2 from '../../components/shared/page-wrapper-v2';
import PageHeader from '../../components/shared/page-header';
import TimeTrackingPageBody from '../../components/shared/time-tracking-page-body';
import styles from './cost-report.module.scss';
import { CostReportFilterBar } from './components/filter-bar';
import CostReportMobileFilterSheet from './components/top-bar/component/mobile-filter-sheet';
import { FilterState } from './components/top-bar/component/filter/types';
import { updateTimeTrackingDateRangeFilter, updateTimeTrackingPropertyFilter } from '../../features/time-tracking-filters';
import { NAVIGATION_ROUTES } from '../../routes/NavigationRoute';
import { useMediaQuery } from 'react-responsive';
import { DeleteDialog } from '../../components/shared/delete-dialog';
import { Lx } from '../../i18n/consts';
import PageFooter from '../../components/shared/page-footer';
import CreateNewButtonV2 from '../../components/shared/create-new-button-v2';
import classNames from 'classnames';
import { filterCostReportItems } from './helpers/filter-cost-report-items';
import AmountSummary from '../../components/shared/amount-summary';
import {
    canManageOtherCosts,
    canManageOwnCosts,
    canSeeCostReportPage,
    canSeeOtherCosts,
} from '../../features/profile/entityPermissions';
import { REmployees } from '../../features/roles/types';

export default function CostReportPage() {
    return (
        <CostReportSearchProvider>
            <CostReportPageContent />
        </CostReportSearchProvider>
    );
}

function CostReportPageContent() {
    const today = useMemo(() => moment(), []);
    const currentYear = today.year();
    const [years, setYears] = useState<Array<Year>>([]);
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const costGridRef = React.useRef<ExpandedGroupsRef>(null);
    // The cost report always groups by employee, so a single fixed scope is enough here.
    const { restoredGroupKeys, armSnapshot } = useGridViewHandoff('costreport', 'employee', costGridRef);

    const employees = useAppSelector((selector) => selector.rEmployees);
    const projects = useAppSelector((selector) => selector.eProjects);
    const profile = useAppSelector((selector) => selector.profile);
    const { workCalendars } = useAppSelector((selector) => selector.workCalendar);
    const propertyFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter.propertyFilter);
    const dateRangeFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter.dateRangeFilter);
    const { data, loading: dataLoading } = useCostReportLoader();
    const { change: searchChange, value: search } = useCostReportSearch();
    const isMobile = useMediaQuery({ maxWidth: 1000 });
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [costPendingDelete, setCostPendingDelete] = useState<Cost | null>(null);
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isMobileCalendarOpen, setMobileCalendarOpen] = useState(false);
    const canViewCostReport = canSeeCostReportPage(profile);
    const canViewOtherCosts = canSeeOtherCosts(profile);
    const canEditOwnCosts = canManageOwnCosts(profile);
    const canEditOtherCosts = canManageOtherCosts(profile);
    const ownEmployee = useMemo<REmployees | null>(() => {
        if (!profile.id) {
            return null;
        }

        return employees.find((employee) => employee.id === profile.id)
            ?? { id: profile.id, name: profile.name, isDefault: false };
    }, [employees, profile.id, profile.name]);
    const costReportEmployees = useMemo<REmployees[]>(() => (
        canViewOtherCosts
            ? employees
            : ownEmployee ? [ownEmployee] : []
    ), [canViewOtherCosts, employees, ownEmployee]);

    const mobileCalendarState = useMemo(() => ({
        open: isMobileCalendarOpen,
        setOpen: (value: boolean) => setMobileCalendarOpen(value),
    }), [isMobileCalendarOpen]);

    const loading = !canViewOtherCosts && costReportEmployees.length < 1;
    const pageLoading = dataLoading || loading;
    const controlsDisabled = false;
    const propertyFilters = useMemo<FilterState>(() => ({
        selectedEmployees: canViewOtherCosts ? propertyFilter?.selectedEmployees ?? [] : [],
    }), [canViewOtherCosts, propertyFilter]);

    const defaultDateRange = useMemo(() => ({
        startDate: today.clone().startOf('month').format('YYYY-MM-DD'),
        endDate: today.clone().endOf('month').format('YYYY-MM-DD'),
    }), [today]);

    const hasSelectedEmployees = canViewOtherCosts && (propertyFilters.selectedEmployees?.length ?? 0) > 0;
    const hasSearch = (search?.trim()?.length ?? 0) > 0;
    const hasCustomDateRange = useMemo(() => {
        if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
            return false;
        }

        return dateRangeFilter.startDate !== defaultDateRange.startDate || dateRangeFilter.endDate !== defaultDateRange.endDate;
    }, [dateRangeFilter?.endDate, dateRangeFilter?.startDate, defaultDateRange]);

    const hasActiveFilters = hasSelectedEmployees || hasSearch || hasCustomDateRange;
    const filteredData = useMemo(
        () => filterCostReportItems(data ?? [], search ?? ''),
        [data, search]
    );
    const totalAmount = useMemo(
        () => filteredData.reduce((sum, item) => sum + (item.amount ?? 0), 0),
        [filteredData]
    );

    const loadCalendarYears = useCallback(async (calendarId: number) => {
        try {
            const loadedYears = await Promise.all(
                [currentYear - 1, currentYear].map((targetYear) =>
                    dispatch(getWorkCalendar({ workCalendarId: calendarId, year: targetYear })).unwrap()
                )
            );

            setYears((currentYears) => {
                const nextYears = [...currentYears];

                loadedYears.forEach((loadedYear) => {
                    if (!nextYears.find((yearItem) => yearItem.year === loadedYear.year)) {
                        nextYears.push(loadedYear);
                    }
                });

                return nextYears;
            });
        } catch (error) {
            console.error('Failed to load work calendar years', error);
        }
    }, [currentYear, dispatch]);

    useEffect(() => {
        const initializeCostReportPage = async () => {
            if (canViewOtherCosts) {
                dispatch(getAllEmployees());
            }

            if (projects.length < 1) {
                dispatch(getProjects());
            }

            const primaryCalendar = workCalendars[0] ?? (await dispatch(getWorkCalendars()).unwrap())[0];
            if (primaryCalendar) {
                await loadCalendarYears(primaryCalendar.id);
            }
        };

        void initializeCostReportPage();
    }, [canViewOtherCosts, dispatch, employees.length, loadCalendarYears, projects.length, workCalendars]);

    useEffect(() => {
        if (canViewOtherCosts) {
            return;
        }

        const selectedEmployees = propertyFilter?.selectedEmployees ?? [];
        if (selectedEmployees.length < 1) {
            return;
        }

        updateTimeTrackingPropertyFilter({
            ...propertyFilter,
            selectedEmployees: [],
        });
    }, [canViewOtherCosts, propertyFilter]);

    useEffect(() => {
        if (!isMobile) {
            setMobileFiltersOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        setIndicatorLoading(dataLoading);
        return () => {
            setIndicatorLoading(false);
        };
    }, [dataLoading]);

    const handleDeleteDialogClose = useCallback(() => {
        setDeleteDialogOpen(false);
        setCostPendingDelete(null);
    }, []);

    const handleDeleteDialogConfirm = useCallback(async () => {
        if (!costPendingDelete) {
            return;
        }

        try {
            await dispatch(deleteCostAction(costPendingDelete.id)).unwrap();
        } catch (error) {
            console.error(error);
        } finally {
            handleDeleteDialogClose();
        }
    }, [costPendingDelete, dispatch, handleDeleteDialogClose]);

    const createNewCost = useCallback((defaults?: Partial<Cost>) => {
        if (!canViewCostReport) {
            return;
        }

        const newCost: Partial<Cost> = {
            employeeId: canEditOtherCosts ? defaults?.employeeId : profile.id,
            date: defaults?.date,
            note: defaults?.note,
            amount: defaults?.amount,
            projectId: defaults?.projectId
        };

        armSnapshot();
        navigate(NAVIGATION_ROUTES.CREATE_COST, { state: { defaults: newCost, employees: costReportEmployees, projects } });
    }, [armSnapshot, canEditOtherCosts, canViewCostReport, costReportEmployees, navigate, profile.id, projects]);

    const getCostForModal = useCallback(async (itemId: number) => {
        return await dispatch(getCost(itemId)).unwrap();
    }, [dispatch]);

    const canManageCostItem = useCallback((item: CostReportItem): boolean => (
        canEditOtherCosts || (canEditOwnCosts && item.employeeId === profile.id)
    ), [canEditOtherCosts, canEditOwnCosts, profile.id]);

    const handleGridCreateCost = useCallback((item: CostReportItem) => {
        createNewCost({
            employeeId: item.employeeId,
            projectId: item.projectId,
            date: item.date,
            note: item.note,
            amount: item.amount,
            files: item.files,
        });
    }, [createNewCost]);

    const handleGridEditCost = useCallback((item: CostReportItem) => {
        if (!canManageCostItem(item)) {
            return;
        }

        const editSelectedCost = async () => {
            const cost = await getCostForModal(item.id);
            if (cost) {
                // Armed only once the cost actually loaded, so a failed fetch leaves no stale snapshot.
                armSnapshot();
                navigate(NAVIGATION_ROUTES.EDIT_COST, { state: { cost } });
            }
        };

        void editSelectedCost();
    }, [armSnapshot, canManageCostItem, getCostForModal, navigate]);

    const handleGridDeleteCost = useCallback((item: CostReportItem) => {
        if (!canManageCostItem(item)) {
            return;
        }

        const prepareDelete = async () => {
            const cost = await getCostForModal(item.id);
            if (cost) {
                setCostPendingDelete(cost);
                setDeleteDialogOpen(true);
            }
        };

        void prepareDelete();
    }, [canManageCostItem, getCostForModal]);

    const handleMobileFilterApply = useCallback((filterState: FilterState) => {
        updateTimeTrackingPropertyFilter({
            ...propertyFilter,
            selectedEmployees: canViewOtherCosts ? filterState.selectedEmployees ?? [] : [],
        });
    }, [canViewOtherCosts, propertyFilter]);

    const handleResetFilters = useCallback(() => {
        updateTimeTrackingPropertyFilter({
            ...propertyFilter,
            selectedEmployees: [],
        });
        searchChange('');
        updateTimeTrackingDateRangeFilter({
            startDate: defaultDateRange.startDate,
            endDate: defaultDateRange.endDate,
        });
    }, [defaultDateRange.endDate, defaultDateRange.startDate, propertyFilter, searchChange]);

    const handleMobileFilterClear = useCallback(() => {
        handleResetFilters();
    }, [handleResetFilters]);

    const toggleMobileFilters = useCallback(() => {
        setMobileFiltersOpen((value) => !value);
    }, []);

    const shouldShowFilterBar = !isMobile;
    const mobileSheetVisible = isMobile && mobileFiltersOpen;
    const shouldRenderMobileDateRow = isMobile && !mobileSheetVisible;
    const shouldShowCreateFooterButton = shouldRenderMobileDateRow;

    useEffect(() => {
        if (!shouldRenderMobileDateRow && isMobileCalendarOpen) {
            setMobileCalendarOpen(false);
        }
    }, [shouldRenderMobileDateRow, isMobileCalendarOpen]);

    const pageBodyWrapperClassName = classNames(styles.pageBodyWrapper, {
        [styles.pageBodyWrapperWithFooter]: shouldShowCreateFooterButton,
    });
    const pageBodyContentClassName = classNames(styles.pageBodyContent, {
        [styles.pageBodyContentMobileCalendarOpen]: isMobile && isMobileCalendarOpen,
    });
    const mobileDateRowClassName = classNames(styles.mobileDateRow, 'cost-report_mobileDateRow', {
        [styles.mobileDateRowCalendarOpen]: isMobileCalendarOpen,
    });

    if (!canViewCostReport) {
        return null;
    }

    return (
        <>
            <PageWrapperV2 className={styles.pageScope}>
                <PageHeader className={styles.pageHeader}>
                    <CostReportTopBar
                        years={years}
                        today={today}
                        onCreateCostClick={createNewCost}
                        onMobileFilterToggle={toggleMobileFilters}
                        mobileFiltersOpen={mobileSheetVisible}
                        hasActiveFilters={hasActiveFilters}
                        disabled={controlsDisabled}
                    />
                </PageHeader>
                <div className={pageBodyWrapperClassName}>
                    <TimeTrackingPageBody justifyContent='flex-start' alignItems='stretch'>
                        <div className={pageBodyContentClassName}>
                            {mobileSheetVisible ? (
                                <CostReportMobileFilterSheet
                                    open={mobileSheetVisible && !controlsDisabled}
                                    onClose={() => setMobileFiltersOpen(false)}
                                    onApply={handleMobileFilterApply}
                                    onClear={handleMobileFilterClear}
                                    search={search ?? ''}
                                    onSearchChange={searchChange}
                                    filters={propertyFilters}
                                    employees={costReportEmployees}
                                    disabled={controlsDisabled}
                                    disableEmployeesFilter={!canViewOtherCosts}
                                />
                            ) : (
                                <>
                                    {shouldRenderMobileDateRow ? (
                                        <div className={styles.mobileHeaderSummary}>
                                            <div className={mobileDateRowClassName}>
                                                <div className={styles.mobileDateSelector}>
                                                    <CostReportDateSelector
                                                        years={years}
                                                        today={today}
                                                        calendarState={mobileCalendarState}
                                                        disabled={controlsDisabled}
                                                    />
                                                </div>
                                            </div>
                                            <div className={styles.mobileSummary}>
                                                <AmountSummary value={totalAmount} className={styles.mobileSummaryAmount} />
                                            </div>
                                        </div>
                                    ) : null}
                                    {shouldShowFilterBar ? (
                                        <CostReportFilterBar
                                            loading={loading}
                                            hasActiveFilters={hasActiveFilters}
                                            onResetFilters={handleResetFilters}
                                            totalAmount={totalAmount}
                                            disabled={controlsDisabled}
                                            employees={costReportEmployees}
                                            disableEmployeesFilter={!canViewOtherCosts}
                                        />
                                    ) : null}
                                    <CostReportGrid
                                        ref={costGridRef}
                                        initialExpandedGroupKeys={restoredGroupKeys}
                                        data={filteredData}
                                        loading={pageLoading}
                                        intl={intl}
                                        onCreateCostClick={handleGridCreateCost}
                                        onEditCostClick={handleGridEditCost}
                                        onDeleteCostClick={handleGridDeleteCost}
                                        canManageCost={canManageCostItem}
                                    />
                                </>
                            )}
                        </div>
                    </TimeTrackingPageBody>
                </div>
                {shouldShowCreateFooterButton ? (
                    <PageFooter>
                        <CreateNewButtonV2 onClick={createNewCost} disabled={controlsDisabled} />
                    </PageFooter>
                ) : null}
            </PageWrapperV2>
            <DeleteDialog
                open={isDeleteDialogOpen}
                onCancel={handleDeleteDialogClose}
                onConfirmDelete={handleDeleteDialogConfirm}
                deleteHeader={intl.formatMessage({ id: Lx.Cost.DELETE_COST })}
                deleteMessage={intl.formatMessage({ id: Lx.Cost.DELETE_COST_INFO })}
                title={intl.formatMessage({ id: Lx.General.COST })}
            />
        </>
    );
}
